import React, { useState, useRef, useCallback } from 'react';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase/config';
import { Upload, File, Trash2, AlertCircle, CheckCircle, X } from 'lucide-react';

const MAX_FILE_SIZE = 1024 * 1024 * 1024; // 1GB
const ACCEPTED_EXTENSIONS = ['.zip', '.stl'];

function DigitalFileUpload({ orderId, user, onFilesChange }) {
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const userId = user?.companyId || user?.uid;

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileExtension = (fileName) => {
    const dot = fileName.lastIndexOf('.');
    return dot !== -1 ? fileName.substring(dot).toLowerCase() : '';
  };

  const validateFile = (file) => {
    const ext = getFileExtension(file.name);
    if (!ACCEPTED_EXTENSIONS.includes(ext)) {
      return `"${file.name}" - .zip 또는 .stl 파일만 업로드 가능합니다.`;
    }
    if (file.size > MAX_FILE_SIZE) {
      return `"${file.name}" - 파일 크기가 1GB를 초과합니다.`;
    }
    return null;
  };

  const uploadFile = useCallback((file) => {
    if (!orderId) {
      setError('주문 ID가 필요합니다.');
      return;
    }

    const fileId = Date.now() + '_' + Math.random().toString(36).substring(2, 9);
    const storagePath = `workOrderFiles/${orderId}/${file.name}`;
    const storageRef = ref(storage, storagePath);
    const uploadTask = uploadBytesResumable(storageRef, file);

    const newFileEntry = {
      id: fileId,
      name: file.name,
      size: file.size,
      progress: 0,
      status: 'uploading', // uploading | complete | error
      downloadURL: null,
      storagePath,
    };

    setFiles((prev) => {
      const updated = [...prev, newFileEntry];
      return updated;
    });

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = Math.round(
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100
        );
        setFiles((prev) => {
          const updated = prev.map((f) =>
            f.id === fileId ? { ...f, progress } : f
          );
          return updated;
        });
      },
      (err) => {
        console.error('파일 업로드 실패:', err);
        setFiles((prev) => {
          const updated = prev.map((f) =>
            f.id === fileId ? { ...f, status: 'error', progress: 0 } : f
          );
          return updated;
        });
      },
      async () => {
        try {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          setFiles((prev) => {
            const updated = prev.map((f) =>
              f.id === fileId
                ? { ...f, status: 'complete', progress: 100, downloadURL }
                : f
            );
            if (onFilesChange) {
              onFilesChange(updated);
            }
            return updated;
          });
        } catch (err) {
          console.error('다운로드 URL 가져오기 실패:', err);
        }
      }
    );
  }, [orderId, onFilesChange]);

  const handleFiles = useCallback((fileList) => {
    setError('');
    const validFiles = [];
    const errors = [];

    Array.from(fileList).forEach((file) => {
      const validationError = validateFile(file);
      if (validationError) {
        errors.push(validationError);
      } else {
        // 중복 이름 확인
        const isDuplicate = files.some((f) => f.name === file.name);
        if (isDuplicate) {
          errors.push(`"${file.name}" - 이미 추가된 파일입니다.`);
        } else {
          validFiles.push(file);
        }
      }
    });

    if (errors.length > 0) {
      setError(errors.join('\n'));
    }

    validFiles.forEach((file) => uploadFile(file));
  }, [files, uploadFile]);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
      e.target.value = '';
    }
  };

  const handleRemoveFile = (fileId) => {
    setFiles((prev) => {
      const updated = prev.filter((f) => f.id !== fileId);
      if (onFilesChange) {
        onFilesChange(updated);
      }
      return updated;
    });
  };

  const styles = {
    container: {
      width: '100%',
    },
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: '600',
      color: '#374151',
      marginBottom: '8px',
    },
    dropZone: {
      border: `2px dashed ${dragOver ? '#6366f1' : '#c7d2fe'}`,
      borderRadius: '12px',
      padding: '32px 20px',
      textAlign: 'center',
      cursor: 'pointer',
      backgroundColor: dragOver ? '#eef2ff' : '#f8f9ff',
      transition: 'all 0.2s ease',
    },
    iconWrapper: {
      display: 'flex',
      justifyContent: 'center',
      marginBottom: '12px',
    },
    dropText: {
      fontSize: '15px',
      fontWeight: '600',
      color: '#4338ca',
      marginBottom: '4px',
    },
    dropSubText: {
      fontSize: '13px',
      color: '#6b7280',
      marginBottom: '12px',
    },
    browseButton: {
      display: 'inline-block',
      padding: '8px 20px',
      backgroundColor: '#6366f1',
      color: '#fff',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      border: 'none',
      transition: 'background-color 0.2s',
    },
    hintText: {
      fontSize: '12px',
      color: '#9ca3af',
      marginTop: '10px',
    },
    errorBox: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '8px',
      marginTop: '10px',
      padding: '10px 14px',
      backgroundColor: '#fef2f2',
      border: '1px solid #fecaca',
      borderRadius: '8px',
    },
    errorText: {
      fontSize: '13px',
      color: '#dc2626',
      whiteSpace: 'pre-line',
      flex: 1,
    },
    errorClose: {
      cursor: 'pointer',
      color: '#dc2626',
      flexShrink: 0,
      marginTop: '1px',
    },
    fileList: {
      marginTop: '12px',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    },
    fileItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '10px 14px',
      backgroundColor: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: '10px',
    },
    fileIconWrapper: {
      width: '36px',
      height: '36px',
      borderRadius: '8px',
      backgroundColor: '#eef2ff',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    fileInfo: {
      flex: 1,
      minWidth: 0,
    },
    fileName: {
      fontSize: '14px',
      fontWeight: '500',
      color: '#1f2937',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    fileMeta: {
      fontSize: '12px',
      color: '#9ca3af',
      marginTop: '2px',
    },
    progressBarOuter: {
      width: '100%',
      height: '4px',
      backgroundColor: '#e5e7eb',
      borderRadius: '2px',
      marginTop: '6px',
      overflow: 'hidden',
    },
    progressBarInner: (progress) => ({
      height: '100%',
      width: `${progress}%`,
      backgroundColor: '#6366f1',
      borderRadius: '2px',
      transition: 'width 0.3s ease',
    }),
    deleteButton: {
      padding: '6px',
      cursor: 'pointer',
      color: '#9ca3af',
      backgroundColor: 'transparent',
      border: 'none',
      borderRadius: '6px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'color 0.2s',
      flexShrink: 0,
    },
    statusIcon: {
      flexShrink: 0,
    },
  };

  return (
    <div style={styles.container}>
      <label style={styles.label}>디지털 파일 업로드</label>

      {/* Drop Zone */}
      <div
        style={styles.dropZone}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <div style={styles.iconWrapper}>
          <Upload size={32} color="#6366f1" />
        </div>
        <div style={styles.dropText}>파일을 여기에 드래그하거나 클릭하세요</div>
        <div style={styles.dropSubText}>STL, OData 파일을 업로드할 수 있습니다</div>
        <button
          type="button"
          style={styles.browseButton}
          onClick={(e) => {
            e.stopPropagation();
            fileInputRef.current?.click();
          }}
        >
          파일 선택
        </button>
        <div style={styles.hintText}>최대 1GB / .zip, .stl 파일 가능</div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".zip,.stl"
          multiple
          style={{ display: 'none' }}
          onChange={handleInputChange}
        />
      </div>

      {/* Error Message */}
      {error && (
        <div style={styles.errorBox}>
          <AlertCircle size={16} color="#dc2626" style={{ flexShrink: 0, marginTop: '1px' }} />
          <span style={styles.errorText}>{error}</span>
          <div style={styles.errorClose} onClick={() => setError('')}>
            <X size={14} />
          </div>
        </div>
      )}

      {/* File List */}
      {files.length > 0 && (
        <div style={styles.fileList}>
          {files.map((file) => (
            <div key={file.id} style={styles.fileItem}>
              <div style={styles.fileIconWrapper}>
                <File size={18} color="#6366f1" />
              </div>
              <div style={styles.fileInfo}>
                <div style={styles.fileName}>{file.name}</div>
                <div style={styles.fileMeta}>
                  {formatFileSize(file.size)}
                  {file.status === 'uploading' && ` - ${file.progress}% 업로드 중...`}
                  {file.status === 'complete' && ' - 업로드 완료'}
                  {file.status === 'error' && ' - 업로드 실패'}
                </div>
                {file.status === 'uploading' && (
                  <div style={styles.progressBarOuter}>
                    <div style={styles.progressBarInner(file.progress)} />
                  </div>
                )}
              </div>
              <div style={styles.statusIcon}>
                {file.status === 'complete' && (
                  <CheckCircle size={18} color="#22c55e" />
                )}
                {file.status === 'error' && (
                  <AlertCircle size={18} color="#ef4444" />
                )}
              </div>
              <button
                type="button"
                style={styles.deleteButton}
                onClick={() => handleRemoveFile(file.id)}
                title="파일 삭제"
                onMouseEnter={(e) => (e.currentTarget.style.color = '#ef4444')}
                onMouseLeave={(e) => (e.currentTarget.style.color = '#9ca3af')}
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default DigitalFileUpload;
